/**
 * The rows a passage's page carries for its words, one per word a reader can
 * look up and save.
 *
 * `partials/yuedu/word-rows.html` draws them inside a `<template>`: the word in
 * every writing, its reading in both scripts, a few words of English and the
 * 词典's save control. Nothing is on the page until a copy is taken, which is
 * what keeps `save-word.ts` from binding and asking about controls it would
 * have no way to tell this page's script about. The list of 生词 takes a whole
 * row and the popover takes its parts.
 */

/** The template holding the rows. */
const templateSelector = "template.yuedu-word-rows";

/** A row, carrying its word on `data-cb-word`. */
const rowSelector = ".yuedu-word-row";

/** The word and its readings, inside a row. */
const entrySelector = ".yuedu-word-entry";

/** The English, inside a row. */
const glossSelector = ".yuedu-word-gloss";

/** The save control, inside a row. */
const saveSelector = ".cb-save-word";

/** One word's row, taken apart for whatever is drawing it. */
export interface YueduWordParts {
  readonly entry: HTMLElement;
  readonly gloss: HTMLElement;
  readonly save: HTMLElement;
}

export class YueduWordRows {
  private readonly rows: ReadonlyMap<string, HTMLElement>;

  constructor(template: HTMLTemplateElement) {
    this.rows = new Map(
      [...template.content.querySelectorAll<HTMLElement>(rowSelector)].map(
        (row) => [row.dataset["cbWord"] ?? "", row],
      ),
    );
  }

  /** The rows on a page, or nothing for a page without them. */
  static found(root: ParentNode): YueduWordRows | undefined {
    const template =
      root.querySelector<HTMLTemplateElement>(templateSelector) ?? undefined;

    return template === undefined ? undefined : new YueduWordRows(template);
  }

  /** Every word with a row, in the order they come in the passage. */
  words(): readonly string[] {
    return [...this.rows.keys()];
  }

  /** A copy of one word's row, to put on the page. */
  row(word: string): HTMLElement | undefined {
    const row = this.rows.get(word);

    return row === undefined ? undefined : (row.cloneNode(true) as HTMLElement);
  }

  /** Copies of one word's parts, or nothing for a word with no row. */
  parts(word: string): YueduWordParts | undefined {
    const row = this.row(word);
    const entry = row?.querySelector<HTMLElement>(entrySelector) ?? null;
    const gloss = row?.querySelector<HTMLElement>(glossSelector) ?? null;
    const save = row?.querySelector<HTMLElement>(saveSelector) ?? null;

    return entry === null || gloss === null || save === null
      ? undefined
      : { entry, gloss, save };
  }
}
